import React, { useLayoutEffect, useRef } from 'react';
import { Phone, MessageSquare } from 'lucide-react';
import { COMPANY_INFO } from '@/constants';

/**
 * Mobile-only sticky bottom action bar (Phase 2C).
 *
 * Per the prompt, this is "the single highest conversion lever for a local
 * service business." Two equal-width buttons: tel: and sms:. Full-width,
 * hidden at ≥768px. Safe-area inset padding so it clears the iOS home bar.
 *
 * The old floating orange circle is replaced by this bar — a user can now
 * initiate either a voice call or a text conversation without pinching or
 * scrolling.
 */

const StickyCallButton: React.FC = () => {
  const barRef = useRef<HTMLDivElement>(null);

  useLayoutEffect(() => {
    const bar = barRef.current;
    const layout = bar?.closest<HTMLElement>('.website-layout');
    if (!bar || !layout) return;

    // Measure the border, wrapped button text and safe-area padding together.
    // Reserve space after the footer, not just after the main route content.
    const measure = () => layout.style.setProperty(
      '--mobile-cta-height', `${Math.ceil(bar.getBoundingClientRect().height)}px`,
    );
    measure();
    const observer = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(measure);
    observer?.observe(bar);
    window.addEventListener('resize', measure);
    return () => {
      observer?.disconnect();
      window.removeEventListener('resize', measure);
      layout.style.removeProperty('--mobile-cta-height');
    };
  }, []);

  const digits = COMPANY_INFO.phone; // "4079707211"
  const smsBody = encodeURIComponent(
    "Hi Elevation Electrical — I'd like a quote.",
  );

  return (
    <div
      ref={barRef}
      data-mobile-contact-bar
      className="
        md:hidden fixed inset-x-0 bottom-0 z-50
        bg-white border-t border-gray-200 shadow-[0_-4px_12px_rgba(0,0,0,0.08)]
      "
      style={{ paddingBottom: 'env(safe-area-inset-bottom, 0px)' }}
      role="region"
      aria-label="Contact Elevation Electrical"
    >
      <div className="grid grid-cols-2 gap-0">
        <a
          href={`tel:${digits}`}
          className="
            flex items-center justify-center gap-2 py-3 px-4
            bg-orange text-navy font-semibold text-sm
            min-h-[48px] active:bg-orange-dark transition-colors
            focus:outline-none focus-visible:ring-2 focus-visible:ring-inset
            focus-visible:ring-white
          "
          aria-label={`Call Elevation Electrical at ${COMPANY_INFO.formattedPhone}`}
          data-analytics-event="click_to_call"
          data-analytics-location="mobile_sticky_bar"
        >
          <Phone className="h-5 w-5 shrink-0" aria-hidden="true" />
          <span className="min-w-0">Call {COMPANY_INFO.formattedPhone}</span>
        </a>
        <a
          href={`sms:${digits}?&body=${smsBody}`}
          className="
            flex items-center justify-center gap-2 py-3 px-4
            bg-navy text-white font-semibold text-sm
            min-h-[48px] active:bg-navy-light transition-colors
            focus:outline-none focus-visible:ring-2 focus-visible:ring-inset
            focus-visible:ring-white
          "
          aria-label="Text Elevation Electrical for a quote"
          data-analytics-event="click_to_text"
          data-analytics-location="mobile_sticky_bar"
        >
          <MessageSquare className="h-5 w-5 shrink-0" aria-hidden="true" />
          Text Us
        </a>
      </div>
    </div>
  );
};

export default StickyCallButton;
